import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Accordion } from 'react-bootstrap';
import { toast } from 'react-toastify';

const emptySection = () => ({ t: '', b: '' });

// ── ALL HELPER COMPONENTS DEFINED OUTSIDE PrivacyPolicyEditor ───────────────
// Defining them inside causes React to treat them as new component types on
// every render, unmounting/remounting DOM nodes and stealing input focus.

const SectionHeader = ({ label }) => (
  <h6 className="text-primary fw-bold text-uppercase small mb-4" style={{ letterSpacing: '1px' }}>
    {label}
  </h6>
);

const FieldRow = ({ label, children }) => (
  <div className="mb-3">
    <Form.Label className="small fw-bold">{label}</Form.Label>
    {children}
  </div>
);

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────

const PrivacyPolicyEditor = ({ workingData, updateSection, onSave, isSaving }) => {

  const [pp, setPp] = useState(() => ({
    heroTitle:       'Privacy Policy',
    heroBgImage:     '',
    showLastUpdated: true,
    introText:       '',
    sections:        [],
    footerNote:      'If you have any questions about this Privacy Policy, please',
    footerLinkText:  'contact us',
    footerLink:      '/contact-us',
    // DB wins
    ...workingData.privacyPolicy,
  }));

  // ── pure local setters ───────────────────────────────────────────────────
  const set = (key, val) => setPp(prev => ({ ...prev, [key]: val }));

  const addSection    = ()            => setPp(prev => ({ ...prev, sections: [...prev.sections, emptySection()] }));
  const removeSection = (idx)         => setPp(prev => ({ ...prev, sections: prev.sections.filter((_, i) => i !== idx) }));
  const updateItem    = (idx, field, val) => setPp(prev => {
    const arr = [...prev.sections];
    arr[idx]  = { ...arr[idx], [field]: val };
    return { ...prev, sections: arr };
  });
  const moveSection   = (idx, dir) => setPp(prev => {
    const arr  = [...prev.sections];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return prev;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    return { ...prev, sections: arr };
  });

  // ── save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    updateSection('privacyPolicy', pp);
    if (onSave) await onSave(pp);
    toast.success('Privacy Policy saved successfully!');
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={11}>
          <Card className="shadow-sm border-0 rounded-4">

            <Card.Header className="bg-white py-3 border-bottom">
              <h5 className="fw-bold mb-0">
                <i className="ri-shield-check-line me-2 text-primary" />
                Privacy Policy — Content Editor
              </h5>
            </Card.Header>

            <Card.Body className="p-0">
              <Accordion flush>

                {/* ── 1. HERO ─────────────────────────────────── */}
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <i className="ri-image-2-line me-2 text-primary" />
                    <strong>Hero Section</strong>
                    <span className="ms-2 text-muted small">— Page banner title &amp; background</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="1. Hero Banner" />
                    <Row className="g-3">
                      <Col md={6}>
                        <FieldRow label="Page Title">
                          <Form.Control
                            value={pp.heroTitle}
                            placeholder="Privacy Policy"
                            onChange={e => set('heroTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Show Last Updated Date">
                          <div className="d-flex align-items-center gap-3 p-3 border rounded bg-light mt-1">
                            <Form.Check
                              type="switch"
                              id="showLastUpdated"
                              checked={pp.showLastUpdated}
                              onChange={e => set('showLastUpdated', e.target.checked)}
                              label={
                                <span className="fw-bold small">
                                  {pp.showLastUpdated ? 'Date shown on page' : 'Date hidden'}
                                </span>
                              }
                            />
                          </div>
                        </FieldRow>
                      </Col>
                      <Col md={12}>
                        <FieldRow label="Background Image URL">
                          <Form.Control
                            value={pp.heroBgImage}
                            placeholder="https://images.unsplash.com/..."
                            onChange={e => set('heroBgImage', e.target.value)}
                          />
                          {pp.heroBgImage && (
                            <img
                              src={pp.heroBgImage}
                              alt="preview"
                              className="mt-2 rounded"
                              style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', opacity: 0.85 }}
                              onError={e => e.target.style.display = 'none'}
                            />
                          )}
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 2. INTRO ────────────────────────────────── */}
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <i className="ri-paragraph me-2 text-primary" />
                    <strong>Intro Paragraph</strong>
                    <span className="ms-2 text-muted small">— Opening statement shown above sections</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="2. Intro Text" />
                    <FieldRow label="Introduction">
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={pp.introText}
                        placeholder="This Privacy Policy describes how we collect, use, and protect your personal information when you use our website and services..."
                        onChange={e => set('introText', e.target.value)}
                      />
                    </FieldRow>
                    <small className="text-muted">
                      <i className="ri-information-line me-1" />
                      Leave empty to hide the intro paragraph. The company name from Branding will auto-fill if you use a fallback.
                    </small>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 3. SECTIONS ─────────────────────────────── */}
                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    <i className="ri-list-ordered me-2 text-primary" />
                    <strong>Policy Sections</strong>
                    <span className="ms-2 text-muted small">
                      — {pp.sections.length} section{pp.sections.length !== 1 ? 's' : ''} added
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="3. Policy Sections" />

                    {pp.sections.length === 0 && (
                      <div className="text-center py-4 text-muted border rounded bg-light mb-3">
                        <i className="ri-file-list-3-line" style={{ fontSize: '32px', display: 'block', marginBottom: '8px', opacity: 0.4 }} />
                        <p className="small mb-0">No sections yet. Click "Add Section" to get started.</p>
                      </div>
                    )}

                    {pp.sections.map((s, i) => (
                      <div key={i} className="border rounded p-3 pt-4 mb-3 bg-light position-relative">

                        {/* Controls — stopPropagation prevents Accordion stealing the click */}
                        <div className="d-flex gap-1" style={{ position: 'absolute', top: '8px', right: '10px', zIndex: 10 }}>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={i === 0}
                            style={{ padding: '2px 7px' }}
                            title="Move up"
                            onClick={(e) => { e.stopPropagation(); moveSection(i, -1); }}
                          >
                            <i className="ri-arrow-up-line" />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={i === pp.sections.length - 1}
                            style={{ padding: '2px 7px' }}
                            title="Move down"
                            onClick={(e) => { e.stopPropagation(); moveSection(i, 1); }}
                          >
                            <i className="ri-arrow-down-line" />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{ padding: '2px 7px' }}
                            onClick={(e) => { e.stopPropagation(); removeSection(i); }}
                          >
                            <i className="ri-delete-bin-line" />
                          </Button>
                        </div>

                        {/* Section number badge */}
                        <div className="mb-2">
                          <span className="badge bg-primary" style={{ fontSize: '11px' }}>Section {i + 1}</span>
                        </div>

                        <Form.Label className="small fw-bold">Section Title</Form.Label>
                        <Form.Control
                          value={s.t}
                          placeholder={`${i + 1}. Information We Collect`}
                          className="mb-2"
                          onChange={e => updateItem(i, 't', e.target.value)}
                        />

                        <Form.Label className="small fw-bold">Section Content</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={s.b}
                          placeholder="Write the full content for this section..."
                          onChange={e => updateItem(i, 'b', e.target.value)}
                        />
                      </div>
                    ))}

                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mt-2 fw-bold d-flex align-items-center gap-2"
                      onClick={addSection}
                    >
                      <i className="ri-add-circle-line" /> Add Section
                    </Button>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 4. FOOTER NOTE ──────────────────────────── */}
                <Accordion.Item eventKey="3">
                  <Accordion.Header>
                    <i className="ri-links-line me-2 text-primary" />
                    <strong>Footer Note</strong>
                    <span className="ms-2 text-muted small">— "Contact us" note at the bottom</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="4. Footer Note" />
                    <Row className="g-3">
                      <Col md={12}>
                        <FieldRow label="Note Text (shown before the link)">
                          <Form.Control
                            value={pp.footerNote}
                            placeholder="If you have any questions about this Privacy Policy, please"
                            onChange={e => set('footerNote', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Link Text">
                          <Form.Control
                            value={pp.footerLinkText}
                            placeholder="contact us"
                            onChange={e => set('footerLinkText', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Link URL">
                          <Form.Control
                            value={pp.footerLink}
                            placeholder="/contact-us"
                            onChange={e => set('footerLink', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      {/* Live preview */}
                      <Col md={12}>
                        <div className="p-3 rounded border bg-light">
                          <small className="text-muted fw-bold d-block mb-1">Preview:</small>
                          <p className="mb-0 small">
                            {pp.footerNote || 'If you have any questions about this Privacy Policy, please'}{' '}
                            <span style={{ color: '#0d6efd', fontWeight: 700 }}>
                              {pp.footerLinkText || 'contact us'}
                            </span>.
                          </p>
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

              </Accordion>
            </Card.Body>

            {/* ── SAVE FOOTER ── */}
            <Card.Footer className="bg-white border-top py-3">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  <i className="ri-information-line me-1 text-primary" />
                  {pp.sections.length} section{pp.sections.length !== 1 ? 's' : ''} ready to publish.
                </small>
                <Button
                  variant="primary"
                  className="px-5 fw-bold shadow"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'SAVE PRIVACY POLICY ✦'}
                </Button>
              </div>
            </Card.Footer>

          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PrivacyPolicyEditor;